import React, { useState } from 'react';
import { Modal, Form, Input, Button, Select, message } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createIssue, createProject, getProjects, IssueWithRelations } from '../../services/issues';
import { getUsers, UserWithStats } from '../../services/users';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faPlus, faUser } from '@fortawesome/pro-solid-svg-icons';
import { Avatar, Space } from 'antd';

const { TextArea } = Input;
const { Option } = Select;

type ProjectOption = { id: string; name: string; status?: string };

interface ProjectPickerProps {
  projects: ProjectOption[];
  value?: string;
  onChange?: (value?: string) => void;
  onBlur?: () => void;
  creating: boolean;
  onCreate: (name: string) => Promise<ProjectOption | undefined>;
}

const ProjectPicker: React.FC<ProjectPickerProps> = ({ projects, value, onChange, onBlur, creating, onCreate }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const selectedProject = projects.find((project) => project.id === value);

  React.useEffect(() => {
    if (!open) setQuery(selectedProject?.name || '');
  }, [open, selectedProject?.name]);

  const trimmedQuery = query.trim();
  const normalizedQuery = trimmedQuery.toLocaleLowerCase();
  const matches = projects.filter((project) =>
    !normalizedQuery || project.name.toLocaleLowerCase().includes(normalizedQuery)
  );
  const exactMatch = projects.find((project) =>
    project.name.trim().toLocaleLowerCase() === normalizedQuery
  );
  const canCreate = Boolean(trimmedQuery) && !exactMatch;

  const selectProject = (project: ProjectOption) => {
    onChange?.(project.id);
    onBlur?.();
    setQuery(project.name);
    setOpen(false);
  };

  const createTypedProject = async () => {
    if (!canCreate || creating) return;
    const project = await onCreate(trimmedQuery);
    if (project) selectProject(project);
  };

  return (
    <div className="queue-project-picker" ref={rootRef}>
      <Input
        value={query}
        placeholder="Select or create a project"
        suffix={<FontAwesomeIcon icon={faMagnifyingGlass} className="queue-project-picker-search-icon" />}
        aria-expanded={open}
        aria-haspopup="listbox"
        onFocus={() => setOpen(true)}
        onBlur={(event) => {
          if (!rootRef.current?.contains(event.relatedTarget as Node | null)) {
            setOpen(false);
            onBlur?.();
          }
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          onChange?.(undefined);
          setOpen(true);
        }}
        onPressEnter={(event) => {
          event.preventDefault();
          if (exactMatch) selectProject(exactMatch);
          else if (canCreate) void createTypedProject();
        }}
      />

      {open && (matches.length > 0 || canCreate) && (
        <div className="queue-project-picker-menu" role="listbox">
          {matches.map((project) => (
            <button
              type="button"
              role="option"
              aria-selected={project.id === value}
              className="queue-project-picker-option"
              key={project.id}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectProject(project)}
            >
              {project.name}
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              className={`queue-project-picker-option queue-project-picker-create${matches.length ? ' queue-project-picker-create--separated' : ''}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => void createTypedProject()}
              disabled={creating}
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>{creating ? 'Creating project…' : `Create project “${trimmedQuery}”`}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

interface CreateIssueModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onCreated?: (issue: IssueWithRelations) => void;
  initialStatus?: string;
}

const CreateIssueModal: React.FC<CreateIssueModalProps> = ({ open, onClose, onSuccess, onCreated, initialStatus = 'BACKLOG' }) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);

  const { data: projects = [] } = useQuery<ProjectOption[]>({
    queryKey: ['projects'],
    queryFn: getProjects,
    enabled: open,
  });

  const { data: users = [] } = useQuery<UserWithStats[]>({
    queryKey: ['users'],
    queryFn: getUsers,
    enabled: open,
  });

  const createMutation = useMutation<IssueWithRelations, Error, Parameters<typeof createIssue>[0]>({
    mutationFn: createIssue,
    onSuccess: (issue) => {
      message.success('Issue created successfully');
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      onClose();
      onSuccess?.();
      if (issue?.id && onCreated) {
        onCreated(issue);
      } else if (issue?.id) {
        window.location.href = `/queue/${issue.id}`;
      }
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to create ticket');
    },
  });

  React.useEffect(() => {
    if (!open) return;
    form.setFieldsValue({ status: initialStatus });
  }, [form, initialStatus, open]);

  const handleCreateProject = async (projectName: string): Promise<ProjectOption | undefined> => {
    if (creatingProject) return undefined;
    setCreatingProject(true);
    try {
      const project = await createProject({ name: projectName });
      queryClient.setQueryData<ProjectOption[]>(['projects'], (current = []) => [project, ...current]);
      message.success(`Project “${project.name}” created`);
      return project;
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to create project');
      return undefined;
    } finally {
      setCreatingProject(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await createMutation.mutateAsync({
        title: values.title,
        description: values.description,
        projectId: values.projectId,
        type: values.type,
        status: values.status || initialStatus,
        priority: values.priority || 'MEDIUM',
        assigneeId: values.assigneeId || undefined,
      });
    } catch {
      // Validation or request error
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="New ticket"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          New ticket
        </Button>,
      ]}
      width={560}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: 'Please enter a title' }]}
        >
          <Input placeholder="Issue title" />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea rows={4} placeholder="Description (optional)" />
        </Form.Item>

        <Form.Item
          name="projectId"
          label="Project"
          rules={[{ required: true, message: 'Please select a project' }]}
          validateTrigger="onBlur"
        >
          <ProjectPicker
            projects={projects}
            creating={creatingProject}
            onCreate={handleCreateProject}
          />
        </Form.Item>

        <Form.Item
          name="type"
          label="Type"
          rules={[{ required: true, message: 'Please select type' }]}
          initialValue="BUG"
        >
          <Select>
            <Option value="BUG">Bug</Option>
            <Option value="FEATURE">Feature</Option>
            <Option value="TASK">Task</Option>
            <Option value="STORY">Story</Option>
            <Option value="EPIC">Epic</Option>
          </Select>
        </Form.Item>

        <Form.Item name="status" label="Status" initialValue="BACKLOG">
          <Select>
            <Option value="BACKLOG">Backlog</Option>
            <Option value="TODO">To Do</Option>
            <Option value="IN_PROGRESS">In Progress</Option>
            <Option value="REVIEW">Review</Option>
            <Option value="TESTING">Testing</Option>
            <Option value="DONE">Done</Option>
          </Select>
        </Form.Item>

        <Form.Item name="priority" label="Priority" initialValue="MEDIUM">
          <Select>
            <Option value="LOWEST">Lowest</Option>
            <Option value="LOW">Low</Option>
            <Option value="MEDIUM">Medium</Option>
            <Option value="HIGH">High</Option>
            <Option value="HIGHEST">Highest</Option>
          </Select>
        </Form.Item>

        <Form.Item name="assigneeId" label="Assignee">
          <Select
            className="assignee-search-select"
            placeholder="Select assignee"
            allowClear
            showSearch
            optionFilterProp="label"
            options={users.map((u: UserWithStats) => ({
              value: u.id,
              label: u.name || u.email,
              user: u,
            }))}
            optionRender={(option) => (
              <Space>
                <Avatar size="small" src={option.data.user?.photoUrl} icon={<FontAwesomeIcon icon={faUser} />} />
                {option.data.label}
              </Space>
            )}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateIssueModal;
